import React from 'react';
import {
  selectors as citationSelectors,
} from '../../redux/citations';
import { parseAuthors } from '../../redux/citations/utils';
import { Dialog } from "@vitalsource/vstui";
import Dispatcher from "../../dispatcher";
import { toastUtils } from "../../utils/toast-utils";
import styled, { withTheme } from "styled-components";
import { cloud } from "@vitalsource/vstui/colors";
import { Tab, Tabs, TabList, TabPanel } from "../../ui/components/tabs";
import { t } from "../../ui/core/translate";
import { CITATION } from '../../redux/citations/selectors';

const CitationTabButton = styled(Tab)`
  text-transform: uppercase;
  font-size: 1.6rem;
  font-weight: 500;
  display: flex;
  margin: 0 10px;
  border-bottom: 3px solid transparent;
  cursor: pointer;
  color: ${cloud.getShade(6)};
  &:first-child {
    margin-left: 0;
  }
  &[aria-selected="true"] {
    color: ${props => props.theme.color.primary.getShade(6)};
    border-bottom: 3px solid ${props => props.theme.color.primary.getShade(6)};
  }
`;

const CitationTabList = styled(TabList)`
  display: flex;
  justify-content: flex-start;
  padding: 5px 0 0 0;
  margin: 0;
`;

const CitationTabPanel = styled(TabPanel)`
  display: flex;
`;

const CitationOutput = styled.div`
  width: 100%;
  padding: 16px 13px;
  font-size: 1.4rem;
  line-height: 1.42;
  background-color: ${cloud.getShade(1)};
  padding-left: 35px ;
  text-indent: -22px ;
  margin-top: 15px;
  &.retrieved-from {
    overflow-wrap: break-word;
    word-wrap: break-word;
    white-space: pre-wrap;
    display: inline-block;
    width: 100%;
    text-indent: 0px;
  }
`;

const CitationTemplate = styled.div`
  font-size: 1.2rem;
  color: ${cloud.getShade(9)};
  line-height: 1.5;
  margin: 16px 13px 18px 13px;
  font-weight: 300;
`;

const CitationFooter = styled.div`
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: center;
  margin: 0 13px;
  &.citation-buttons {
    white-space: nowrap;
    button:not(:last-child) {
      margin-right: 6px;
    }
  }
`;

const CitationCheckAccuracy = styled.div`
  font-size: 1.2rem;
  color: ${cloud.getShade(9)};
  line-height: 1.2;
  font-weight: 300;
  padding-right: 10px;
`;

class Citations extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      format: (Fraction.Helpers.userData().citationsFormat || CITATION.mla)
    };

    this._copyCitation = this._copyCitation.bind(this);
    this._getFormattedCitationText = this._getFormattedCitationText.bind(this);
    this._closeModal = this._closeModal.bind(this);
    this._loadCitationInfo = this._loadCitationInfo.bind(this)
  }

  componentDidMount() {
    this._loadCitationInfo()
  }

  _loadCitationInfo() {
    this.props.book.viewer.Book.getCitationInfo(this.props.book.isbn, (resp) => {
      if(resp.isSuccess()) {
        let citationInfo = resp.data.citation;
        this.setState({
          citationInfo: {
            ...citationInfo,
            isbn: this.props.book.isbn,
            author: parseAuthors(citationInfo.contributors),
            date: citationInfo.copyrightDate || citationInfo.publicationDate
          }
        })
      }else{
        this.setState({citationInfo: null})
      }
    })
  }

  _closeModal() {
    this.props.onClose
      ? this.props.onClose()
      : Dispatcher.emit(Dispatcher.CLOSE_MODAL);
  }

  _getFormattedCitationText() {
    const citationInfo = this.state.citationInfo;
    if(citationInfo){
      Fraction.Helpers.saveUserData("citationsFormat", this.state.format);
      return citationSelectors.getCitationText(citationInfo, this.state.format)
    }else{
      return i18n.t("base.loading");
    }
  }

  _copyCitation() {
    Fraction.Helpers.copyText(this._getFormattedCitationText());
    toastUtils.notify(
      i18n.t('citations.citation-copied'),
      { type: "success" }
    );
    this._closeModal();
  }

  getCitationTabs(){
    const tabs = [];
    tabs.push({
      tabID: CITATION.mla,
      tabText: t("citations.mla", "MLA"),
      tabComponent: this.getCitation()
    });
    tabs.push({
      tabID: CITATION.apa,
      tabText: t("citations.apa", "APA"),
      tabComponent: this.getCitation()
    });
    tabs.push({
      tabID: CITATION.har,
      tabText: t("citations.harvard", "Harvard"),
      tabComponent: this.getCitation()
    });
    tabs.push({
      tabID: CITATION.van,
      tabText: t("citations.vancouver", "Vancouver"),
      tabComponent: this.getCitation()
    });
    if (Fraction.Brand.Citations.ABNT) {
      tabs.push({
        tabID: CITATION.abnt,
        tabText: t("citations.abnt", "ABNT"),
        tabComponent: this.getCitation()
      });
    }

    return tabs;
  }

  getCitation() {
    let citationFormat = i18n.t(`citations.${this.state.format}-format`);
    if (this.state.format === 'abnt') {
      citationFormat = 'Sobrenome, Inicial do Primeiro Nome. Inicial do nome do meio.” <span class=‘bold’>Título do livro digital</span>. Local da Editora: Nome da Editora, Ano da Publicação. ISBN [número]. Disponível em: [URL]. Acesso em: [data de acesso].';
    }

    return (
      <div>
        <CitationOutput dangerouslySetInnerHTML={{ __html: this._getFormattedCitationText() }} />
        <CitationTemplate dangerouslySetInnerHTML={{ __html: citationFormat }} />
        <CitationFooter>
          <CitationCheckAccuracy>
            {i18n.t('citations.check-accuracy')}
          </CitationCheckAccuracy>
        </CitationFooter>
      </div>
    );
  }

  renderCitationTabContent() {
    const tabs = this.getCitationTabs();

    const selectedIndex = tabs.findIndex(tab =>
      tab.tabID === this.state.format
    );

    const TabsContainer = (
      <Tabs
        selectedIndex={Math.max(0, selectedIndex)}
        onSelect={index => this.setState({ format: tabs[index].tabID })}
      >
        <CitationTabList>
          {tabs.map(tab => (
            <CitationTabButton key={`tab-${tab.tabID}`}>
              {tab.tabText}
            </CitationTabButton>
          ))}
        </CitationTabList>
        <div>
          {tabs.map(tab => (
            <CitationTabPanel key={`panel-${tab.tabID}`}>
              {tab.tabComponent}
            </CitationTabPanel>
          ))}
        </div>
      </Tabs>
    );
    return TabsContainer;
  }

  render() {
    return (
        <Dialog
          closeButtonLabel={i18n.t('base.close')}
          open
          primaryActionButton={{
            label: i18n.t('reader.notebook.copy'),
            onClick: this._copyCitation,
          }}
          secondaryActionButton={{
            label: i18n.t('base.cancel'),
            onClick: this._closeModal,
          }}
          onRequestClose={this._closeModal}
          size="large"
          rootSelector="#bookshelf"
          title={i18n.t('citations.citation')}
        >
        <div className="citations">
          {this.renderCitationTabContent()}
        </div>
      </Dialog>
    );
  }
}


export default withTheme(Citations)
